import React, { useState } from 'react';
import { Phone, Send, Mail, MapPin, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from '../hooks/use-toast';
import { COMPANY } from '../mock';

const ContactCTA = () => {
  const [form, setForm] = useState({ name: '', phone: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast({ title: 'Please fill required fields', description: 'Name and phone are required.' });
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('name', form.name);
      fd.append('phone', form.phone);
      fd.append('email', form.email);
      fd.append('message', form.message);
      fd.append('_subject', `New quote request from ${form.name} - Singh Logistics WA`);
      fd.append('_captcha', 'false');
      fd.append('_template', 'table');

      // FormSubmit relays the form data to the recipient email
      await fetch('https://formsubmit.co/ajax/karam@singhlogisticswa.com.au', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: fd,
      });

      toast({
        title: 'Quote Request Sent',
        description: `Thanks ${form.name}! We\u2019ll contact you shortly at ${form.phone}.`,
      });
      setForm({ name: '', phone: '', email: '', message: '' });
    } catch (err) {
      toast({
        title: 'Could not send',
        description: 'Please call us directly at ' + COMPANY.phone,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative py-20 bg-[#0a324e] text-white overflow-hidden">
      <div
        className="absolute inset-0 opacity-10 bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1779517226000-77e096450fa8?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80')",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-4 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <h2 className="text-3xl md:text-4xl font-bold">Ready to Get Started?</h2>
          <p className="mt-4 text-slate-200 text-lg max-w-xl">
            Contact Singh Logistics WA today for reliable 24/7 refrigerated
            transport across Perth and Western Australia.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <a href={`tel:${COMPANY.phoneRaw}`}>
              <Button size="lg" className="h-12 bg-white text-[#0d3d5e] hover:bg-slate-100 rounded-md font-semibold">
                <Phone className="w-5 h-5 mr-2" /> Call {COMPANY.phone}
              </Button>
            </a>
            <a href={`mailto:${COMPANY.email}`}>
              <Button size="lg" variant="outline" className="h-12 border-2 border-white text-white bg-transparent hover:bg-white hover:text-[#0d3d5e] rounded-md font-semibold">
                <Mail className="w-5 h-5 mr-2" /> Email Us
              </Button>
            </a>
          </div>

          <div className="mt-10 space-y-3 text-slate-200">
            <div className="flex items-center gap-3">
              <MapPin className="w-5 h-5 text-white/70 shrink-0" /> {COMPANY.address}
            </div>
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-white/70 shrink-0" />{' '}
              <a href={`mailto:${COMPANY.email}`} className="hover:text-white">
                {COMPANY.email}
              </a>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-white/70 shrink-0" />{' '}
              <a href={`tel:${COMPANY.phoneRaw}`} className="hover:text-white">
                {COMPANY.phone}
              </a>
            </div>
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-white/70 shrink-0" /> {COMPANY.hours}
            </div>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white text-slate-900 rounded-lg p-6 md:p-8 shadow-2xl"
        >
          <h3 className="text-xl font-bold text-[#0d3d5e]">Request a Quote</h3>
          <p className="text-sm text-slate-500 mt-1">
            We&rsquo;ll get back to you within minutes during business hours.
          </p>

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Name*
              </label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Your name"
                className="h-11"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Phone*
              </label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+61 ..."
                className="h-11"
              />
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Email
            </label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="you@company.com"
              className="h-11"
            />
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Delivery Details
            </label>
            <Textarea
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="Tell us about your delivery needs..."
              rows={4}
            />
          </div>
          <Button
            type="submit"
            disabled={submitting}
            className="mt-5 w-full h-12 bg-[#0d3d5e] hover:bg-[#0a324e] text-white rounded-md font-semibold disabled:opacity-70"
          >
            <Send className="w-4 h-4 mr-2" />{' '}
            {submitting ? 'Sending...' : 'Send Request'}
          </Button>
          <p className="mt-3 text-xs text-slate-500 text-center">
            Sent directly to {COMPANY.email}
          </p>
        </form>
      </div>
    </section>
  );
};

export default ContactCTA;
